import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Envelope } from '@phosphor-icons/react';
import { mapAuthError } from '@mybusinessmate/api-client';
import { signInInputSchema, type SignInInput } from '@mybusinessmate/schema';
import { AuthHeading } from '../../../../../components/auth/auth-heading';
import { BackLink } from '../../../../../components/auth/back-link';
import { Banner } from '../../../../../components/ui/banner';
import { Button } from '../../../../../components/ui/button';
import { Input, PasswordInput } from '../../../../../components/ui/input';
import { textLinkClass } from '../../../../../components/ui/text-link';
import { authClient } from '../../../../../lib/api';
import { useOnboardingStore } from '../../../../../lib/onboarding-store';
import { isStaffRole, refreshSession, SESSION_NOT_KEPT_MESSAGE } from '../../../../../lib/session';

export const Route = createFileRoute('/subdomain/app/_auth/sign-in/email')({
  component: SignInEmailPage,
});

function minutes(seconds: number | undefined): string {
  return seconds ? `${Math.max(1, Math.ceil(seconds / 60))} minute${seconds > 60 ? 's' : ''}` : 'a little while';
}

function SignInEmailPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setPendingEmail = useOnboardingStore((s) => s.setPendingEmail);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignInInput>({
    resolver: zodResolver(signInInputSchema),
    mode: 'onBlur',
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async ({ email, password }) => {
    const { data, error } = await authClient.signIn.email({ email, password });

    const mapped = mapAuthError(error);
    if (mapped) {
      switch (mapped.kind) {
        case 'invalid_credentials':
          setError('password', { message: 'That password doesn’t match. Try again or reset it.' });
          return;
        case 'email_not_verified':
          // Sign-in does not resend the code (sendOnSignIn is off), so do it now and continue at verify.
          await authClient.emailOtp.sendVerificationOtp({ email, type: 'email-verification' });
          setPendingEmail(email);
          await navigate({ to: '/subdomain/app/verify-email' });
          return;
        case 'account_locked':
        case 'rate_limited':
          setError('root', { message: `Too many attempts. Try again in ${minutes(mapped.retryAfterSeconds ?? 900)}.` });
          return;
        case 'suspended':
          setError('root', { message: 'This account is suspended. Please contact support.' });
          return;
        default:
          setError('root', { message: mapped.message });
          return;
      }
    }

    // A correct password on a 2FA account is answered with a challenge, not a session.
    if (data && 'twoFactorRedirect' in data && data.twoFactorRedirect) {
      await navigate({ to: '/subdomain/app/two-factor' });
      return;
    }

    // Read the new session for real before navigating, or the route guard still sees "signed out".
    const session = await refreshSession(queryClient);
    if (!session) {
      // The password was accepted but the browser never got (or won't send back) the session cookie.
      setError('root', { message: SESSION_NOT_KEPT_MESSAGE });
      return;
    }
    if (isStaffRole(session.user.role)) {
      // A staff account has no business in the client portal: end that session now so it cannot linger.
      await authClient.signOut();
      await refreshSession(queryClient);
      setError('root', { message: 'That’s a staff account. Staff sign in at the staff app.' });
      return;
    }
    await navigate({ to: '/subdomain/app' });
  });

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-(--mbm-space-6)">
      <BackLink to="/subdomain/app/sign-in">All sign-in options</BackLink>
      <AuthHeading title="Welcome back" subtitle="Sign in to see how your business is doing." />

      {errors.root ? <Banner tone="error">{errors.root.message}</Banner> : null}

      <Input
        label="Email"
        type="email"
        autoComplete="email"
        leadingIcon={Envelope}
        error={errors.email?.message}
        {...register('email')}
      />

      <div className="flex flex-col gap-(--mbm-space-2)">
        <PasswordInput
          label="Password"
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password')}
        />
        <Link to="/subdomain/app/forgot-password" className={`${textLinkClass} self-end`}>
          Forgot password?
        </Link>
      </div>

      <Button type="submit" fullWidth loading={isSubmitting}>
        Sign in
      </Button>

      <p className="text-center text-body-small text-text-secondary">
        New here?{' '}
        <Link to="/subdomain/app/welcome" className={textLinkClass}>
          Create an account
        </Link>
      </p>
    </form>
  );
}
